import { auditionPageKey, type CatalogVoiceReview, type VoiceSampleIdentity } from './voiceCatalogReviews.ts';

export type AuditionState = {
  currentId: string | null; preparingId: string | null; playingId: string | null;
  verifyingId: string | null; pagePlaying: boolean; canResume: boolean;
};
export const EMPTY_AUDITION: AuditionState = { currentId: null, preparingId: null, playingId: null, verifyingId: null, pagePlaying: false, canResume: false };
export type AuditionAudio = Pick<HTMLAudioElement, 'play' | 'pause' | 'onended' | 'onerror' | 'ended'>;
type Dependencies = {
  savedSample: (voice: VoiceSampleIdentity) => Promise<string>;
  createAudio: (url: string) => AuditionAudio;
  verify: (voice: VoiceSampleIdentity, eventId: string) => Promise<CatalogVoiceReview>;
  onVerified: (review: CatalogVoiceReview) => void;
  onState: (state: AuditionState) => void;
  onError: (message: string) => void;
  requestId: () => string;
  isVerified?: (voice: VoiceSampleIdentity) => boolean;
  timeoutMs?: number;
  playbackTimeoutMs?: number;
};
type PageRun = { key: string; voices: readonly VoiceSampleIdentity[]; cursor: number };

/** A page-bounded saved-audio player. Cancellation owns every async boundary;
 * only a natural ended event can create a central listening-review timestamp. */
export class CatalogAudition {
  private state: AuditionState = { ...EMPTY_AUDITION };
  private sequence = 0;
  private disposed = false;
  private audio: AuditionAudio | null = null;
  private cancelPlayback: (() => void) | null = null;
  private page: PageRun | null = null;
  private deps: Dependencies;
  constructor(deps: Dependencies) { this.deps = deps; }

  private owns(sequence: number) { return !this.disposed && sequence === this.sequence; }
  private emit(patch: Partial<AuditionState> = {}) {
    this.state = { ...this.state, ...patch, canResume: !!this.page && this.page.cursor < this.page.voices.length };
    if (!this.disposed) this.deps.onState({ ...this.state });
  }
  stop(resetPage = false, clearSelection = false) {
    this.sequence++;
    this.cancelPlayback?.(); this.cancelPlayback = null;
    if (this.audio) { this.audio.onended = null; this.audio.onerror = null; this.audio.pause(); this.audio = null; }
    if (resetPage) this.page = null;
    this.emit({ preparingId: null, playingId: null, verifyingId: null, pagePlaying: false, ...(clearSelection ? { currentId: null } : {}) });
  }
  dispose() { this.disposed = true; this.stop(true, true); }
  select(voiceId: string) { this.stop(); this.emit({ currentId: voiceId }); }
  setPageContext(voices: readonly VoiceSampleIdentity[]) {
    if (this.page && this.page.key !== auditionPageKey(voices)) this.stop(true, true);
    else if (this.state.currentId && !voices.some(v => v.id === this.state.currentId)) this.stop(true, true);
  }
  async toggleVoice(voice: VoiceSampleIdentity) {
    if (this.state.currentId === voice.id && (this.state.playingId || this.state.preparingId || this.state.verifyingId)) { this.stop(); return; }
    this.stop(true); this.deps.onError('');
    const sequence = this.sequence;
    await this.playOne(voice, sequence);
    if (this.owns(sequence)) this.stop();
  }
  async playPage(voices: readonly VoiceSampleIdentity[]) {
    if (this.disposed || !voices.length) return;
    voices = voices.slice(0, 50);
    const key = auditionPageKey(voices);
    const cursor = this.page?.key === key && this.page.cursor < voices.length ? this.page.cursor : 0;
    this.stop(); this.deps.onError('');
    // Snapshot only the visible page. Never fetch or advance to another page.
    const run: PageRun = { key, voices: voices.map(v => ({ ...v })), cursor };
    this.page = run; this.emit({ pagePlaying: true });
    const sequence = this.sequence;
    while (this.owns(sequence) && run.cursor < run.voices.length) {
      // Check at the point of playback as well as at the initial page snapshot.
      // A review loaded or completed while paused must not be played again.
      if (this.deps.isVerified?.(run.voices[run.cursor])) { run.cursor++; continue; }
      if (!await this.playOne(run.voices[run.cursor], sequence) || !this.owns(sequence)) return;
      run.cursor++;
    }
    if (this.owns(sequence)) this.stop();
  }
  private async bounded<T>(promise: Promise<T>): Promise<T> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      return await Promise.race([promise, new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(Error('Request timed out')), this.deps.timeoutMs ?? 15000);
      })]);
    } finally { if (timer !== undefined) clearTimeout(timer); }
  }
  private async playOne(voice: VoiceSampleIdentity, sequence: number): Promise<boolean> {
    let stage: 'sample' | 'play' | 'verify' = 'sample';
    this.emit({ currentId: voice.id, preparingId: voice.id, playingId: null, verifyingId: null });
    try {
      if (!voice.sample_audio_url) throw Error('No saved sample');
      const url = await this.bounded(this.deps.savedSample(voice));
      if (!this.owns(sequence)) return false;
      stage = 'play';
      const player = this.deps.createAudio(url); this.audio = player;
      const ended = await new Promise<boolean>((resolve, reject) => {
        let settled = false;
        let deadline: ReturnType<typeof setTimeout> | undefined;
        const cleanup = () => {
          if (deadline !== undefined) clearTimeout(deadline);
          player.onended = null; player.onerror = null;
          if (this.audio === player) this.audio = null;
          if (this.cancelPlayback === cancel) this.cancelPlayback = null;
        };
        const finish = (naturalEnd: boolean, failure?: Error) => {
          if (settled) return;
          settled = true; cleanup();
          if (!naturalEnd) player.pause();
          if (failure) reject(failure); else resolve(naturalEnd);
        };
        const cancel = () => finish(false);
        this.cancelPlayback = cancel;
        deadline = setTimeout(() => finish(false, Error('Saved sample playback timed out')), this.deps.playbackTimeoutMs ?? 45000);
        player.onended = () => { if (this.owns(sequence) && player.ended) finish(true); };
        player.onerror = () => finish(false, Error('Saved audio playback failed'));
        void player.play().then(() => {
          if (!this.owns(sequence)) { player.pause(); return; }
          if (!settled) this.emit({ preparingId: null, playingId: voice.id });
        }).catch(() => finish(false, Error('Saved audio playback was blocked')));
      });
      if (!ended || !this.owns(sequence)) return false;
      stage = 'verify'; this.emit({ preparingId: null, playingId: null, verifyingId: voice.id });
      const review = await this.bounded(this.deps.verify(voice, this.deps.requestId()));
      // The sample already finished. A later Stop prevents queue advancement,
      // but must not discard a successfully saved review of that completed clip.
      if (!this.disposed) this.deps.onVerified(review);
      if (!this.owns(sequence)) return false;
      this.emit({ verifyingId: null }); return true;
    } catch {
      if (!this.owns(sequence)) return false;
      this.stop();
      this.deps.onError(stage === 'verify'
        ? `Unable to save the Verified timestamp for ${voice.display_name}. Refresh to check before continuing.`
        : `Unable to play the saved sample for ${voice.display_name}. Please try again.`);
      return false;
    }
  }
}
